import type { HTMLAttributes, ReactNode } from 'react';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import styles from './Modal.module.scss';

export type ModalSize = 'sm' | 'md' | 'lg';

export interface ModalProps {
  open: boolean;
  title?: ReactNode;
  size?: ModalSize;
  closeOnOverlay?: boolean;
  closable?: boolean;
  onClose: () => void;
  children?: ReactNode;
}

function ModalRoot({
  open,
  title,
  size = 'md',
  closeOnOverlay = true,
  closable = true,
  onClose,
  children,
}: ModalProps) {
  useEffect(() => {
    if (!open) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', onKeydown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', onKeydown);
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className={styles.overlay}
      onMouseDown={event => {
        if (closeOnOverlay && event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className={clsx(styles.container, styles[size])}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
      >
        {(title || closable) && (
          <div className={styles.header}>
            <h3 className={styles.title}>{title}</h3>
            {closable && (
              <button
                type="button"
                className={styles.close}
                onClick={onClose}
                aria-label="Close modal"
              >
                ×
              </button>
            )}
          </div>
        )}
        {children}
      </div>
    </div>,
    document.body
  );
}

export interface ModalBodyProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
}

function ModalBody({ className, children, ...rest }: ModalBodyProps) {
  return (
    <div className={clsx(styles.body, className)} {...rest}>
      {children}
    </div>
  );
}

export interface ModalFooterProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
}

function ModalFooter({ className, children, ...rest }: ModalFooterProps) {
  return (
    <div className={clsx(styles.footer, className)} {...rest}>
      {children}
    </div>
  );
}

type ModalComponent = typeof ModalRoot & {
  Body: typeof ModalBody;
  Footer: typeof ModalFooter;
};

export const Modal = Object.assign(ModalRoot, {
  Body: ModalBody,
  Footer: ModalFooter,
}) as ModalComponent;
